import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Api from '../../services/Api';
import SecuredComponent from "./SiteEditElements/SecuredComponent";
import Person from "./Person";
import DimensionPicker from "./SiteEditElements/DimensionPicker";
import { DefaultButton, Link, MessageBar, MessageBarType, PrimaryButton, Separator, Spinner, SpinnerSize, Pivot, PivotItem, Icon, TooltipHost, TextField, Checkbox } from "@fluentui/react";
import { PermissionRole, PowerAppEnvironmentBusinessType, PowerAppEnvironmentType, PowerAppProvisioningStatus } from "../../GlobalConst/globalEnums";
import PowerAppEnvironmentTasks from "./PowerAppEnvironmentTasks";
import { mapEnumToOptions, renderDate } from "../../helpers/HelperMethods";
import CustomTextField from "./SiteEditElements/CustomTextField";
import LoadingIcon from "../formFields/LoadingIcon";
import CustomDropDown from "./SiteEditElements/CustomDropDown";
import axios from "axios";
import InfoTooltip from "../formFields/InfoTooltip";

const CancelToken = axios.CancelToken;

const PowerAppEnvironmentEditor = ({ environmentId, onRequestClose, callBack, onEditChange, isEditable }) => {

    const currentUser = useSelector((state) => state.user.currentUser)
    const cancelSource = useRef(null);

    const [environment, setEnvironment] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [editable, setEditable] = useState(isEditable);
    const [updateSet, setUpdateSet] = useState([]);
    const [myOwnEnvironment, setMyOwnEnvironment] = useState(true);
    const [error, setError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [messageVisible, setMessageVisible] = useState(false);
    const [childrenValidation, setChildrenValidation] = useState({})
    const [isChildrenUpdateValid, setIsChildrenUpdateValid] = useState(true);
    const [isRefreshed, setIsRefreshed] = useState(false)
    const [isOwnerValidating, setIsOwnerValidating] = useState(false)
    const [ownerError, setOwnerError] = useState(null)

    useEffect(() => {
        cancelSource.current = CancelToken.source();

        getEnvironment(environmentId);

        return () => {
            cancelSource.current.cancel();
        }
    }, [environmentId]);

    useEffect(() => {
        setIsChildrenUpdateValid(Object.keys(childrenValidation).reduce((value, key) => {
            return value && childrenValidation[key];
        }, true))
    }, [childrenValidation])

    useEffect(() => {
        onEditChange(editable)
    }, [editable])

    const isMyEnvironment = (environment) => {
        return (environment.owner || {}).userId === currentUser.id || environment.isOwnedViaServiceLeads || environment.isOwnedViaSystemAdmins
    };

    const isEnvEditable = environment && (currentUser.role === PermissionRole.administrator || (environment.owner || {}).userId === currentUser.id || environment.isOwnedViaServiceLeads)

    // const loadAppWithoutOwners = (environment) => {
    //     let newApp = { ...environment };

    //     newApp.primaryOwner = getValue('primaryOwner', true, environment)
    //     newApp.secondaryOwner = getValue('secondaryOwner', true, environment)

    //     setUpdateSet([])
    //     setEnvironment(newApp);
    // }

    const getEnvironment = (id) => {
        setIsLoading(true);
        Api.get(`/EnvironmentsPanel?id=${id}`, { cancelToken: cancelSource.current.token })
            .then(res => res.data)
            .then(environment => {
                setEnvironment(environment)

                setMyOwnEnvironment(isMyEnvironment(environment));
                setIsLoading(false);

                if (callBack)
                    callBack(environment)
            });
    };

    const onUpdateChildrenValidation = (key, isValid) => {
        setChildrenValidation(valid => ({ ...valid, [key]: isValid }))
    }

    const refresh = () => {
        getEnvironment(environmentId);
        setIsRefreshed(true)
    };

    const updateChange = (key, value, callBack) => {
        let set = updateSet.filter(x => x.key !== key);
        if (environment[key] === value || (environment[key] === null && value === '')) {
            setUpdateSet(set);
            if (callBack) callBack();
            return;
        }
        set.push({ key, value });
        setUpdateSet(set);
        if (callBack) callBack();
    };

    const updatePersonChange = (key, value, isLimited, callBack) => {
        setUpdateSet((prevSet) => {
            let set = prevSet.filter(x => x.key !== key);
            if (isLimited)
                value = value.length ? value[0] : null

            if ((environment[key] || {}).userId === (value || {}).id) {
                if (callBack) callBack();
                return set;
            }
            set.push({ key, value: isLimited ? { value } : value });
            if (callBack) callBack();
            return set
        })
    }

    const updateDimensionChange = (key, keyId, value, idField = 'id', isNullableObject = false, callBack) => {
        setUpdateSet((prevSet) => {
            const isMultiValue = Array.isArray(value)

            let set = prevSet.filter(x => x.key !== key && x.key !== keyId);
            const parsedValue = isMultiValue
                                ? value.map(x => x[idField]) 
                                : isNullableObject
                                    ? (value ? {value: value[idField]} : {value: null})
                                    : (value ? value[idField] : 0)
            const parsedDefaultValue = isMultiValue 
                                        ? environment[key].map(x => x[idField]) 
                                        : environment[key]
                                            ? environment[key][idField]
                                            : 0;
                            
            if (JSON.stringify(parsedDefaultValue) === JSON.stringify(parsedValue)) {
                if (callBack) callBack();
                    return set;
            }
            set.push({ key, value });
            set.push({ 
                key: keyId, 
                value: parsedValue
            });
            if (callBack) callBack();
            return set
        })
    }

    const editableClick = () => {
        setEditable(true);
    };

    const eraseSelection = () => {
        setEditable(false);
        setError(false);
    };

    const buildUpdate = () => {
        let command = { id: environmentId };
        updateSet.forEach(x => command[x.key] = x.value);

        return command;
    };

    const updateEnvironment = (ev) => {
        setError(false);
        setMessageVisible(false);
        setIsLoading(true);
        setIsSaving(true);
        eraseSelection();

        Api.post('UpdatePowerAppEnvironmentRequest', buildUpdate())
            .then(res => res.data)
            .then(post => {
                setIsLoading(false);
                setIsSaving(false);
                if (post === 200) {
                    setMessageVisible(true);
                    refresh();
                } else {
                    setError(true);
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const saveEnabled = () => {
        return !isLoading
            && updateSet.length !== 0
            && !isSaving
            //&& (new Date(environment.expirationDate) > Date.now() || getUpdatedValue('extension'))
            && isChildrenUpdateValid
            && !editEnabled()
            && editable
            && !isOwnerValidating
    };

    const editEnabled = () => {
        return !isRefreshed
            && !isSaving
            && !editable
        //&& (currentUser.role === PermissionRole.administrator || currentUser.role === PermissionRole.contributor)
    };

    const getValue = (key, isNullableObject = false, defaultSet = null) => {
        const result = getChange(key)
        if (result)
            return isNullableObject ? (result.value || {}).value : result.value
        else
            return defaultSet ? defaultSet[key] : environment[key]
    }

    const disabled = !editable;

    const btnIconEdit = environment && isEnvEditable &&
        <Icon style={{ fontSize: 17 }} iconName='edit' onClick={editableClick} />

    const messageBar =
        <div>
            {
                error &&
                <MessageBar
                    dismissButtonAriaLabel="Close"
                    truncated={true}
                    messageBarType={MessageBarType.error}>
                    We cannot proceed your changes.
                </MessageBar>
            }
            {
                messageVisible &&
                <MessageBar
                    dismissButtonAriaLabel="Close"
                    truncated={true}
                    messageBarType={MessageBarType.success}>
                    Your changes have been saved.
                </MessageBar>
            }
        </div>

    const environmentTitle = environment &&
        <div className="site-editor-header">
            <div className="site-editor-title" title={environment.name}>
                {environment.name.length > 53 ? `${environment.name.substr(0, 50)}...` : environment.name}
            </div>
            <div className="site-editor-icon" onClick={onRequestClose}><Icon iconName="chromeClose" /></div>
            {editEnabled() && <div className="site-editor-icon">{btnIconEdit}</div>}
            {messageBar}
        </div>

    const getChange = (key) => updateSet.find(x => x.key === key)

    //const isValid = (key) => childrenValidation[key] === undefined || childrenValidation[key]

    // const isIcofrLimited = currentUser.role !== PermissionRole.administrator && environment && environment.isIcofr

    // const icofrWarningMessage = 
    //     isIcofrLimited &&
    //     <div>For ICoFR environments, ownership transfer must be requested by contacting support team: <a href={`mailto:global-storeandsharepowerplatformsupport@abb.com?subject=${`Ownership change requested for ${environment.environment.name} ${environment.type} ${environment.name}`}`}>global-storeandsharepowerplatformsupport@abb.com</a>.</div>

    const getProductionEnvironment = () => {
        const updateValue = getChange('prodEnvironment')
        if (updateValue)
            return updateValue.value

        const key = getValue('prodEnvironmentId');
        const value = getValue('prodEnvironmentName');

        return key ? { key, value } : null;
    }

    const isProdEnvironmentPickerDisabled = () => {
        const businessType = getValue('businessType', true)
        return !businessType || businessType === PowerAppEnvironmentBusinessType.prod || businessType === PowerAppEnvironmentBusinessType.personalProductivity || getValue('type') === PowerAppEnvironmentType.developer
    }

    const isBusinessTypePickerDisabled = () => {
        return getValue('type') === PowerAppEnvironmentType.developer || getValue('businessType', true) === PowerAppEnvironmentBusinessType.personalProductivity
    }

    const getOwnerError = () => {
        const owner = getValue('owner', true)

        if (owner && (owner.isExternal || owner.isServiceAccount))
            return 'Owner must be an internal ABB employee.'
        else
            return ownerError
    }

    const onOwnerUpdate = async (user) => {
        updatePersonChange('owner', user, true)
        setOwnerError(null)
        
        if (user.length) {
            if (user[0].id !== (environment.owner || {}).userId) {
                if (environment.securityGroup) {
                    const isValid = await validateGroupMembership(user[0].id, environment.securityGroup.userId)
                    if (!isValid) {
                        setOwnerError(<span><b>New owner must be a member of the environment's security group.</b><br/> Adding them as an <b>Owner</b> is strongly recommended for full access.</span>);
                    }
                } else if (environment.dataverseUrl) {
                    const isValid = await validateDataverseMembership(user[0].id, environment.internalId, environment.dataverseUrl)
                    if (!isValid) {
                        setOwnerError(<span>Ownership cannot be transferred: The selected user is not part of this environment.</span>);
                    }
                }
            }

            if (user[0].management) {
                updateDimensionChange('management', 'managementId', user[0].management)
                onUpdateChildrenValidation('management', true)
            }
        }
    }

    const validateGroupMembership = async (userId, groupId) => {
        setIsOwnerValidating(true);
        var result = await Api.get(`/EntryValidation/ValidateGroupMembership?userId=${userId}&groupId=${groupId}`)
        setIsOwnerValidating(false);

        return result.data
    }

    const validateDataverseMembership = async (userId, environmentId, dataverseUrl) => {
        setIsOwnerValidating(true);
        var result = await Api.get(`/EntryValidation/ValidateEnvironmentUser?userId=${userId}&environmentId=${environmentId}&environmentDataverseUrl=${dataverseUrl}`)
        setIsOwnerValidating(false);

        return result.data
    }

    const isPendingOwnerChange = 
        environment
        && (environment.provisioningStatus === PowerAppProvisioningStatus.pendingApproval || environment.provisioningStatus === PowerAppProvisioningStatus.updating)

    const isOwnerFieldDisabled = environment && (
        disabled 
        || environment.type === PowerAppEnvironmentType.developer 
        || isPendingOwnerChange
    )

    const editor = environment &&
        <div>
            <div className="site-editor" style={{ display: 'flex', flexDirection: 'column' }}>
                <SecuredComponent
                    readSecurityLevel='Administrator'
                    readOnlyAccess={myOwnEnvironment}>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsLargerSingleCol}>
                            <label className='item-title'>Name</label>
                            {
                                environment.url
                                    ? <Link
                                        target='_blank'
                                        href={environment.url}
                                        styles={{ root: { display: 'block', padding: '8px', backgroundColor: 'rgb(243, 242, 241)' } }}
                                    >
                                        {environment.name}
                                    </Link>
                                    : <TextField
                                        autoComplete="off"
                                        type="text"
                                        disabled={true}
                                        value={environment.name}
                                    // styles={{field: {color: 'rgb(50, 49, 48)'}}}
                                    />
                            }
                        </div>
                        <div style={twoColumnsSmallerSingleCol}>
                            <label className='item-title'>Type</label>
                            <TextField styles={{ field: { color: 'rgb(84, 84, 84)' } }} disabled={true} value={environment.type} />
                        </div>
                    </div>
                </SecuredComponent>
                <SecuredComponent
                    readSecurityLevel='Administrator'
                    readOnlyAccess={myOwnEnvironment}>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsLargerSingleCol}>
                            <label className='item-title'>Environment ID</label>
                            <TextField styles={{ field: { color: 'rgb(84, 84, 84)' } }} disabled={true} value={environment.internalId} />
                        </div>
                        <div style={twoColumnsSmallerSingleCol}>
                            <label className='item-title'>Created on</label>
                            <TextField styles={{ field: { color: 'rgb(84, 84, 84)' } }} disabled={true} value={renderDate(environment.created)} />
                        </div>
                    </div>
                </SecuredComponent>
                <SecuredComponent
                    readSecurityLevel='Administrator'
                    readOnlyAccess={myOwnEnvironment}>
                    <p style={{margin: '20px 0 5px'}}>View-Only Environment Settings</p>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleCol}>
                            <Checkbox
                                label='Dataverse enabled'
                                checked={environment.hasDataverseDatabase}
                                disabled={true}
                                styles={checkBoxStyles}
                            />
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <Checkbox
                                label='Managed Environment'
                                checked={environment.isManaged}
                                disabled={true}
                                styles={checkBoxStyles}
                            />
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <Checkbox
                                label='ICoFR Environment'
                                checked={environment.isIcofr}
                                disabled={true}
                                styles={checkBoxStyles}
                            />
                        </div>
                    </div>
                </SecuredComponent>
                <SecuredComponent
                    readSecurityLevel='Administrator'
                    editSecurityLevel='Administrator'
                    unlimitedAccess={myOwnEnvironment}>                    
                    <div style={{marginTop: 10}}>
                        <Checkbox
                            label='Recertification enabled'
                            checked={getValue('isRecertificationEnabled')}
                            disabled={disabled}
                            styles={checkBoxStyles}
                            onChange={(e, v) => updateChange('isRecertificationEnabled', v)}
                        />
                    </div>
                </SecuredComponent>
                <SecuredComponent
                    readSecurityLevel='Administrator'
                    readOnlyAccess={myOwnEnvironment}>
                    <div>
                        <Person
                            userType={<span>Security Group <InfoTooltip content='The security group that controls access to this environment.' /></span>}
                            disabled={true}
                            user={getValue('securityGroup', true)}
                            limited={true}
                        />
                    </div>
                </SecuredComponent>
                <Separator styles={separatorStylesNoBelowSpace} />
                <SecuredComponent
                    readSecurityLevel='Administrator'
                    editSecurityLevel='Administrator'
                    unlimitedAccess={myOwnEnvironment}>

                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleCol}>
                            <Person
                                userType={<span>Owner {getValue('type') === PowerAppEnvironmentType.developer && <InfoTooltip content='Ownership cannot be transferred for Developer environments, as they are limited to a single user.' />}</span>}
                                updateType="owner"
                                onChange={onOwnerUpdate}
                                disabled={isOwnerFieldDisabled}
                                user={getValue('owner', true)}
                                isRequired={true && !isPendingOwnerChange}
                                limited={true}
                                onValidation={(v) => onUpdateChildrenValidation('owner', v)}
                                errorMessage={!isPendingOwnerChange && getOwnerError()}
                                includeManagementInfo={true}
                                isValidating={isOwnerValidating}
                            />
                            {
                                isPendingOwnerChange &&
                                <TooltipHost content='Updates are in progress' styles={pendingOwnerChangesIconStyles}>
                                    <Icon iconName='workflow' style={updatesIconStyles} />
                                </TooltipHost>
                            }
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <DimensionPicker
                                itemSelector={(v) => updateDimensionChange('management', 'managementId', v)}
                                searchTitle="Business owner organization"
                                searchDataUrl='/Management'
                                isDisabled={disabled}
                                selection={getValue('management')}
                                isRequired={true}
                                treeDataUrl='/Management'
                                onValidation={(v) => onUpdateChildrenValidation('management', v)}
                            />
                        </div>
                    </div>
                </SecuredComponent>
                <SecuredComponent
                    readSecurityLevel='Administrator'
                    editSecurityLevel='Administrator'
                    unlimitedAccess={myOwnEnvironment}>
                    <div>
                        <label className='item-title'>Description</label>
                        <CustomTextField
                            defaultValue={environment.description}
                            disabled={disabled}
                            onUpdate={(v) => updateChange('description', v)}
                            multiline={true}
                        />
                    </div>
                </SecuredComponent>
                <Separator styles={separatorStyles} />
                <div style={twoColumnsContainer}>
                    <div style={twoColumnsSmallerSingleCol}>
                        <label className='item-title'>Environment Business Type <InfoTooltip content="Select the current environment's relationship to the production environment." /></label>
                        <CustomDropDown
                            defaultValue={environment.businessType}
                            options={mapEnumToOptions({preDev:"PreDev", dev:"Dev", stage:"Stage", prod:"Prod"})}
                            onUpdate={(v) => updateChange('businessType', v)}
                            disabled={disabled || isBusinessTypePickerDisabled()}
                            onValidation={(v) => onUpdateChildrenValidation('businessType', v)}
                            required={false}
                        />                               
                    </div>
                    <div style={twoColumnsLargerSingleCol}>
                        <DimensionPicker
                            itemSelector={(v) => updateDimensionChange('prodEnvironment', 'prodEnvironmentId', v, 'key', true)}
                            searchTitle={<span>Production Environment <InfoTooltip content='Select the production environment associated with the current environment. Only environments with the same owner can be linked.' /></span>}
                            searchDataUrl={`/PowerAppEnvironmentSearch?ownerId=${environment.owner ? environment.owner.id : 0}`}
                            isDisabled={disabled || isProdEnvironmentPickerDisabled()}
                            selection={isProdEnvironmentPickerDisabled() ? null : getProductionEnvironment()}
                            isRequired={false}
                            treeDataUrl={`/PowerAppEnvironmentSearch?ownerId=${environment.owner ? environment.owner.id : 0}`}
                            onValidation={(v) => onUpdateChildrenValidation('prodEnvironmentId', v)}
                            isMultiValue={false}
                            keyHidden={true}
                            emptyMessage='No production environment with the business type "Prod" is available for the current owner.'
                        />
                    </div>
                </div>
                <Separator styles={separatorStyles} />
                <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                    {
                        <SecuredComponent readSecurityLevel='Administrator'
                            editSecurityLevel='Administrator'
                            unlimitedAccess={myOwnEnvironment && isEnvEditable}>
                            <div style={{ width: '100%', display: "flex", justifyContent: "space-between" }}>
                                <div>
                                    {
                                        editEnabled() &&
                                        <DefaultButton className="edit-icon" onClick={editableClick}> Edit </DefaultButton>
                                    }
                                    {
                                        editable &&
                                        <PrimaryButton className="edit-icon" onClick={updateEnvironment} disabled={!saveEnabled()}> Save </PrimaryButton>
                                    }
                                </div>
                            </div>
                        </SecuredComponent>
                    }
                </div>
            </div>
            <div className="edit-panel">
                {
                    isSaving &&
                    <Spinner style={{ float: 'left', marginLeft: 20 }} size={SpinnerSize.medium} />
                }
            </div>
        </div>

    const envIcons = environment &&
        <div style={envIconStyles}>
            {
                environment.isIcofr && 
                <TooltipHost content='ICoFR Environment'>
                    <Icon iconName='AllCurrency' styles={{root: {fontSize: 22}}} />
                </TooltipHost> 
            }
            {
                environment.isManaged && 
                <TooltipHost content='Managed Environment'>
                    <Icon iconName='ProductionFloorManagement' styles={{root: {fontSize: 24}}} />
                </TooltipHost>
            }
            {
                // environment.isStrictlyConfidential && 
                // <TooltipHost content='Strictly Confidential Environment'>
                //     <Icon iconName='Permissions' />
                // </TooltipHost>
            }
        </div>

    return (
        isLoading && !environment
            ? <LoadingIcon />
            :
            <div style={{ height: '100%' }}>
                {environmentTitle}
                <div className='site-editor-container'>
                    <div style={iconsContainerStyles}>
                        {envIcons}
                    </div>
                    <Pivot defaultSelectedKey='Details'>
                        <PivotItem itemKey='Details' headerText="Details">
                            {editor}
                        </PivotItem>
                    {
                        environment && environment.changeTasks.length && (currentUser.role === PermissionRole.administrator || environment.isOwnedViaServiceLeads) &&
                        <PivotItem itemKey='Tasks' headerText="Tasks">
                            <PowerAppEnvironmentTasks environment={environment} refresh={() => refresh(true)} />
                        </PivotItem>
                    }
                    </Pivot>
                </div>
            </div>
    );
}

// const sitesOwnerStyle = {
//     width: '100%',
//     marginTop: '10px',
//     //float: 'left',
//     marginRight: '70px'
// }

const iconsContainerStyles = {
    height: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'relative',
    right: 18,
    top: 12,
}

const envIconStyles = {
    fontSize: 22,
    zIndex: 1,
    marginLeft: 5,
    display: 'flex',
    gap: 10,
    cursor: 'pointer'
}

const separatorStyles = {
    root: {
        margin: '10px 0'
    }
}

const separatorStylesNoBelowSpace = {
    root: {
        margin: '10px 0 -5px'
    }
}

const twoColumnsContainer = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 20,
}

const twoColumnsSingleCol = {
    flex: 1,
    overflow: 'hidden'
}

const twoColumnsLargerSingleCol = {
    flex: 3,
    overflow: 'hidden'
}

const twoColumnsSmallerSingleCol = {
    flex: 2,
    overflow: 'hidden'
}

const checkBoxStyles = {
    root: {
        margin: '10px 0'
    },
    text: {
        color: 'rgb(50, 49, 48)'
    }
}

const pendingOwnerChangesIconStyles = {
    root: {
        position: 'relative',
        bottom: 33,
        left: 275,
        cursor: 'default'
    }
}

const updatesIconStyles = {
    color: 'orange'
}

export default PowerAppEnvironmentEditor


// WEBPACK FOOTER //
// ./src/components/EditSite/PowerAppEnvironmentEditor.js